import { withIcon } from './Icon.tsx';
import { faCheck, faMinus } from '@fortawesome/pro-regular-svg-icons';
import React, { useEffect, useRef } from 'react';
import classnames from '../utils/classnames.tsx';
import { mergeRefs } from '../utils/mergeRefs.tsx';

const CheckIcon = withIcon(faCheck);
const MinusIcon = withIcon(faMinus);

type CheckboxSize = 'default' | 'sm';

interface CheckboxBoxProps {
    state: 'checked' | 'unchecked' | 'indeterminate';
    isDisabled?: boolean;
    // sm where rows are dense: tables, menus, filter lists
    size?: CheckboxSize;
    className?: string;
    children?: React.ReactNode;
}

// the box alone, for an element that is the checkbox itself (a menu's checkbox item)
export const CheckboxBox = ({ state, isDisabled = false, size = 'default', className, children }: CheckboxBoxProps) => {
    const isOn = state !== 'unchecked';
    return (
        <span
            data-disabled={isDisabled ? 'disabled' : undefined}
            data-state={state}
            className={classnames(
                'relative flex flex-none items-center justify-center select-none rounded border border-solid transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-line-brand-strong',
                size === 'sm' ? 'h-4 w-4 text-[0.625rem]' : 'h-5 w-5 text-sm',
                isDisabled ? 'pointer-events-none' : 'cursor-pointer',
                isDisabled && isOn && 'border-line-strong bg-line-strong text-content-white dark:text-content-black',
                isDisabled && !isOn && 'border-line-weak bg-surface-medium',
                !isDisabled &&
                    isOn &&
                    'border-line-transparent bg-surface-brand-strong-default text-content-white dark:text-content-black',
                // a row around the box can be the group, so hovering its label highlights the box too
                !isDisabled &&
                    !isOn &&
                    'border-line-strong bg-surface-weak shadow-control hover:border-line-hover group-hover:border-line-hover',
                className
            )}
        >
            {children}
            {state === 'indeterminate' && <MinusIcon />}
            {state === 'checked' && <CheckIcon />}
        </span>
    );
};

export interface CheckboxProps extends Omit<React.ComponentPropsWithRef<'input'>, 'size'> {
    indeterminate?: boolean;
    size?: CheckboxSize;
}

const Checkbox = ({
    checked,
    indeterminate = false,
    disabled,
    size = 'default',
    className,
    ref,
    ...props
}: CheckboxProps) => {
    const inputRef = useRef<HTMLInputElement>(null);
    useEffect(() => {
        if (inputRef.current) inputRef.current.indeterminate = indeterminate;
    }, [indeterminate]);

    return (
        <CheckboxBox
            state={indeterminate ? 'indeterminate' : checked ? 'checked' : 'unchecked'}
            isDisabled={disabled}
            size={size}
            className={className}
        >
            <input
                type="checkbox"
                disabled={disabled}
                checked={checked}
                {...props}
                ref={mergeRefs(ref, inputRef)}
                className="inset-0 absolute opacity-0 outline-none cursor-[inherit]"
            />
        </CheckboxBox>
    );
};

export default Checkbox;
